// src/components/KeyFocusAreas.tsx
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Target, Loader2 } from 'lucide-react';
import './KeyFocusAreas.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

// Cache for focus areas by patient MRN
interface FocusAreasCache {
  [mrn: string]: {
    focusAreas: string;
    hasHandoff: boolean;
  };
}

interface KeyFocusAreasProps {
  patientMRN: string;
  patientName: string;
  department: string;
  currentNurseId: string;
  cachedFocusAreas: FocusAreasCache;
  onCacheUpdate: (mrn: string, data: { focusAreas: string; hasHandoff: boolean }) => void;
}

// Helper function to convert markdown to HTML
const renderMarkdown = (text: string): string => {
  if (!text) return '';
  
  let html = text;
  
  // Convert headers
  html = html.replace(/^## (.*$)/gim, '<h3 class="focus-heading">$1</h3>');
  html = html.replace(/^# (.*$)/gim, '<h2 class="focus-heading">$1</h2>');
  
  // Convert bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
  
  // Convert italic
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  html = html.replace(/_(.*?)_/g, '<em>$1</em>');
  
  // Convert bullet points
  html = html.replace(/^- (.*$)/gim, '<li>$1</li>');
  html = html.replace(/^\* (.*$)/gim, '<li>$1</li>');
  
  // Wrap consecutive li elements in ul
  html = html.replace(/(<li>.*<\/li>\n?)+/g, (match) => `<ul class="focus-list">${match}</ul>`);
  
  // Convert line breaks
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br/>');
  
  return html;
};

const KeyFocusAreas: React.FC<KeyFocusAreasProps> = ({ 
  patientMRN, 
  patientName, 
  department,
  currentNurseId,
  cachedFocusAreas,
  onCacheUpdate
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [focusAreas, setFocusAreas] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasHandoff, setHasHandoff] = useState(false);
  const [error, setError] = useState<string>('');

  // Show for all departments (removed Rehab-only restriction)

  useEffect(() => {

    // Check if we have cached data for this patient
    const cached = cachedFocusAreas[patientMRN];
    if (cached) {
      setFocusAreas(cached.focusAreas);
      setHasHandoff(cached.hasHandoff);
      return;
    }

    const fetchFocusAreas = async () => {
      if (!patientMRN) return;

      setIsLoading(true);
      setError('');
      
      try {
        // First, fetch the previous handoff
        const handoffResponse = await fetch(
          `${BACKEND_URL}/handoff/latest-by-type/${patientMRN}?exclude_nurse_id=${encodeURIComponent(currentNurseId)}`
        );
        
        if (!handoffResponse.ok) {
          throw new Error('Failed to fetch handoff');
        }
        
        const handoffData = await handoffResponse.json();
        
        // Check if there's any handoff (audio or generated)
        const handoffText = handoffData.generated_handoff?.handoff_text || 
                           handoffData.audio_handoff?.handoff_text || '';
        
        if (!handoffText) {
          setHasHandoff(false);
          setFocusAreas('');
          // Cache the result
          onCacheUpdate(patientMRN, { focusAreas: '', hasHandoff: false });
          setIsLoading(false);
          return;
        }
        
        setHasHandoff(true);
        
        // Now analyze the handoff for focus areas
        const analysisResponse = await fetch(`${BACKEND_URL}/handoff/analyze-focus-areas`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            handoff_text: handoffText,
            patient_name: patientName,
            department: department
          })
        });
        
        const analysisData = await analysisResponse.json();
        
        if (analysisData.success && analysisData.focus_areas) {
          setFocusAreas(analysisData.focus_areas);
          // Cache the result
          onCacheUpdate(patientMRN, { focusAreas: analysisData.focus_areas, hasHandoff: true });
        } else {
          setError(analysisData.message || 'Failed to analyze handoff');
        }
      } catch (err) {
        console.error('Error fetching focus areas:', err);
        setError('Failed to load focus areas');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFocusAreas();
  }, [patientMRN, patientName, department, currentNurseId, cachedFocusAreas, onCacheUpdate]);

  // Don't render if no previous handoff exists
  if (!hasHandoff && !isLoading) {
    return null;
  }

  return (
    <div className={`key-focus-container ${isExpanded ? 'expanded' : ''}`}>
      <button 
        className="key-focus-header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="key-focus-title-row">
          <Target size={20} className="focus-icon" />
          <div className="key-focus-title-text">
            <span className="key-focus-title">Recent Changes / Key Areas of Focus</span>
            <span className="key-focus-subtitle">Action items for your shift</span>
          </div>
        </div>
        {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      
      {isExpanded && (
        <div className="key-focus-content">
          {isLoading ? (
            <div className="focus-loading">
              <Loader2 size={20} className="spin" />
              <span>Analyzing previous handoff...</span>
            </div>
          ) : error ? (
            <div className="focus-error">
              <span>{error}</span>
            </div>
          ) : focusAreas ? (
            <div 
              className="focus-areas-text"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(focusAreas) }}
            />
          ) : (
            <div className="focus-empty">
              <span>No focus areas identified</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KeyFocusAreas;
